import { createContext } from "react";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";

/**
 * Input for adding an item to a seller cart.
 */
export interface AddToCartInput {
  /** The seller offer ID to add */
  offer_id: string;
  /** Quantity to add */
  quantity: number;
  /** Optional metadata for the line item */
  metadata?: Record<string, unknown>;
  /** Set by Quick Buy callers (add, then straight to checkout); analytics only. */
  quickBuy?: boolean;
}

/**
 * Simplified cart context for the multi-seller cart architecture.
 *
 * In this architecture:
 * - Each customer can have one active cart per seller
 * - The API automatically routes items to the correct seller's cart
 * - No need to track a single cartId locally - use `useSellerCarts()` query
 */
export interface CartContextValue {
  /**
   * Adds an item to the appropriate seller cart.
   * Creates a new cart if one doesn't exist for that seller.
   *
   * @returns The cart_id, seller_id, and whether a new cart was created
   */
  addToCart: (
    input: AddToCartInput
  ) => Promise<SalonStockerHttpTypes.StoreAddToSellerCartResponse>;

  /**
   * Whether an add-to-cart operation is in progress
   */
  isAddingToCart: boolean;
}

export const CartContext = createContext<CartContextValue | null>(null);
